import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { useToast } from '../hooks/use-toast';
import axios from 'axios';
import { ArrowLeft, Download, Loader2, CheckCircle, AlertCircle, Play, RefreshCw } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const getAuthHeader = () => {
  const token = localStorage.getItem('admin_token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const ContentImporter = () => {
  const [importing, setImporting] = useState(false);
  const [results, setResults] = useState(null);
  const [scraperStatus, setScraperStatus] = useState(null);
  const [checkingStatus, setCheckingStatus] = useState(false);
  const [startingScraper, setStartingScraper] = useState(false);
  const { toast } = useToast();
  const pollRef = useRef(null);

  const checkScraperStatus = useCallback(async () => {
    setCheckingStatus(true);
    try {
      const res = await axios.get(`${API}/admin/scraper-status`, { headers: getAuthHeader() });
      setScraperStatus(res.data);
      if (res.data.is_running && !pollRef.current) {
        pollRef.current = setInterval(async () => {
          try {
            const r = await axios.get(`${API}/admin/scraper-status`, { headers: getAuthHeader() });
            setScraperStatus(r.data);
            if (!r.data.is_running) {
              clearInterval(pollRef.current);
              pollRef.current = null;
            }
          } catch (e) { console.error('Scraper status poll error:', e); }
        }, 10000);
      }
    } catch (error) {
      console.error('Failed to check scraper status:', error);
    } finally {
      setCheckingStatus(false);
    }
  }, []);

  useEffect(() => {
    checkScraperStatus();
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, [checkScraperStatus]);

  const startScraper = async () => {
    setStartingScraper(true);
    try {
      const res = await axios.post(`${API}/admin/start-scraper`, {}, { headers: getAuthHeader() });
      toast({ title: "Scraper", description: res.data.message });
      setTimeout(checkScraperStatus, 3000);
    } catch (error) {
      toast({ title: "Error", description: "Failed to start scraper", variant: "destructive" });
    } finally {
      setStartingScraper(false);
    }
  };

  const handleImport = async () => {
    setImporting(true);
    setResults(null);
    try {
      const res = await axios.post(`${API}/import-content`, {}, { headers: getAuthHeader() });
      setResults(res.data);
      toast({ title: "Import Successful!", description: `Imported ${res.data.imported} new posts` });
    } catch (error) {
      toast({ title: "Import Failed", description: error.response?.data?.detail || "An error occurred", variant: "destructive" });
    } finally {
      setImporting(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center gap-4">
            <Link to="/admin/dashboard">
              <Button variant="outline" size="sm">
                <ArrowLeft size={16} className="mr-2" />
                Back to Dashboard
              </Button>
            </Link>
            <h1 className="text-2xl font-bold text-slate-900">Import Content</h1>
          </div>
        </div>
      </header>

      <div className="max-w-4xl mx-auto px-6 py-12 space-y-8">
        {/* Real Scraper - Primary */}
        <div className="bg-white rounded-xl p-8 shadow-sm border border-gray-100">
          <div className="text-center mb-6">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-cyan-100 rounded-full mb-4">
              <Download className="text-cyan-600" size={32} />
            </div>
            <h2 className="text-2xl font-bold text-slate-900 mb-2" data-testid="scraper-title">
              Import from Paisley.org.uk
            </h2>
            <p className="text-gray-600">
              Scrapes real posts from paisley.org.uk in the background. Slow and steady - it can handle thousands of posts.
            </p>
          </div>

          {/* Status Panel */}
          {scraperStatus && (
            <div className="bg-slate-50 rounded-lg p-5 mb-6 border border-slate-200">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-semibold text-slate-900">Database Status</h3>
                <div className="flex items-center gap-2">
                  {scraperStatus.is_running && (
                    <span className="inline-flex items-center gap-1.5 text-sm text-green-700 bg-green-100 px-3 py-1 rounded-full">
                      <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></span>
                      Scraper Running
                    </span>
                  )}
                  <Button variant="ghost" size="sm" onClick={checkScraperStatus} disabled={checkingStatus}>
                    <RefreshCw size={14} className={checkingStatus ? 'animate-spin' : ''} />
                  </Button>
                </div>
              </div>

              {/* Progress bar */}
              {scraperStatus.progress && scraperStatus.progress.total_target > 0 && (
                <div className="mb-4">
                  <div className="flex justify-between text-sm text-gray-600 mb-1">
                    <span>{scraperStatus.progress.urls_processed || 0} / {scraperStatus.progress.total_target} URLs processed</span>
                    <span>{scraperStatus.progress.sitemap_progress}</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-3">
                    <div
                      className="bg-cyan-500 h-3 rounded-full transition-all duration-500"
                      style={{ width: `${Math.min(100, ((scraperStatus.progress.urls_processed || 0) / scraperStatus.progress.total_target) * 100)}%` }}
                    ></div>
                  </div>
                  <div className="flex justify-between text-xs text-gray-500 mt-1">
                    <span>Imported: {scraperStatus.progress.imported_this_run || 0} new</span>
                    <span>Skipped: {scraperStatus.progress.skipped_duplicates || 0} duplicates</span>
                  </div>
                </div>
              )}

              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
                <div className="bg-white rounded-lg p-3 text-center border">
                  <div className="text-2xl font-bold text-cyan-600" data-testid="total-posts-count">
                    {scraperStatus.total_posts}
                  </div>
                  <div className="text-xs text-gray-500 mt-1">Total Posts</div>
                </div>
                {Object.entries(scraperStatus.breakdown || {}).filter(([_, count]) => count > 0).map(([cat, count]) => (
                  <div key={cat} className="bg-white rounded-lg p-3 text-center border">
                    <div className="text-lg font-semibold text-slate-700">{count}</div>
                    <div className="text-xs text-gray-500 mt-1 capitalize">{cat}</div>
                  </div>
                ))}
              </div>

              {scraperStatus.last_log && (
                <details className="mt-3">
                  <summary className="text-sm text-gray-500 cursor-pointer hover:text-gray-700">View scraper log</summary>
                  <pre className="mt-2 text-xs bg-slate-900 text-green-400 p-3 rounded-lg overflow-x-auto max-h-48 overflow-y-auto whitespace-pre-wrap">
                    {scraperStatus.last_log}
                  </pre>
                </details>
              )}
            </div>
          )}

          <div className="flex justify-center gap-4">
            <Button
              onClick={startScraper}
              disabled={startingScraper || (scraperStatus && scraperStatus.is_running)}
              size="lg"
              data-testid="start-scraper-btn"
              className="bg-cyan-500 hover:bg-cyan-600 text-white px-8 py-6 text-base"
            >
              {startingScraper ? (
                <><Loader2 className="mr-2 animate-spin" size={20} /> Starting...</>
              ) : scraperStatus?.is_running ? (
                <><Loader2 className="mr-2 animate-spin" size={20} /> Scraper Running...</>
              ) : (
                <><Play className="mr-2" size={20} /> Start Paisley.org.uk Import</>
              )}
            </Button>
            <Button variant="outline" size="lg" onClick={checkScraperStatus} className="px-6 py-6" data-testid="refresh-status-btn">
              <RefreshCw size={18} className="mr-2" /> Refresh Status
            </Button>
          </div>

          <div className="mt-6 bg-amber-50 border border-amber-200 rounded-lg p-4">
            <p className="text-sm text-amber-900">
              <strong>How it works:</strong> The scraper runs in the background, importing posts page by page with a 2-second delay between pages. It saves progress so it can resume if interrupted. You can close this page and check back later.
            </p>
          </div>
        </div>

        {/* Quick Import - Secondary */}
        <div className="bg-white rounded-xl p-8 shadow-sm border border-gray-100">
          <h2 className="text-xl font-bold text-slate-900 mb-2">Quick Import (Sample Content)</h2>
          <p className="text-gray-600 mb-6 text-sm">
            Quickly add 55 sample posts across categories for testing. These are template posts, not real scraped content.
          </p>
          <Button
            onClick={handleImport}
            disabled={importing}
            variant="outline"
            size="lg"
            data-testid="quick-import-btn"
            className="px-8"
          >
            {importing ? (
              <><Loader2 className="mr-2 animate-spin" size={18} /> Importing...</>
            ) : (
              <><Download className="mr-2" size={18} /> Add 55 Sample Posts</>
            )}
          </Button>

          {results && (
            <div className={`mt-4 rounded-lg p-4 ${results.success ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'}`}>
              <div className="flex items-start gap-2">
                {results.success ? <CheckCircle className="text-green-600 mt-0.5" size={18} /> : <AlertCircle className="text-red-600 mt-0.5" size={18} />}
                <div>
                  {results.success ? (
                    <p className="text-sm text-green-800">Imported {results.imported} posts. Total: {results.total}</p>
                  ) : (
                    <p className="text-sm text-red-800">{results.message || 'Import failed'}</p>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ContentImporter;
